import {
  isValidElement,
  type AnchorHTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
} from 'react';
import { isElement } from 'react-is';

import type {
  _AriaButtonProps as AriaButtonProps,
  _ButtonAria as ButtonAria,
} from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type StylingProps,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';

import { useTimeframeSelectorContext } from './hooks/useTimeframeSelectorContext.js';
import { useFormFieldMessagesContext } from '../../forms/forms-core/hooks/useFormFieldMessagesContext.js';

/**
 * @public
 */
export type TimeframeSelectorCustomTriggerProps = AriaLabelingProps &
  StylingProps &
  DataTestId & {
    /** Elements to be displayed in the CustomTrigger. */
    children:
      | ReactNode
      | undefined
      | ((
          customTriggerProps: {
            displayValue: ReactNode;
            isInvalid: boolean;
            hint: string;
          },
          props:
            | (AriaLabelingProps &
                StylingProps &
                DataTestId &
                (Partial<ButtonAria<AnchorHTMLAttributes<HTMLButtonElement>>> &
                  AriaButtonProps))
            | null,
        ) => ReactElement);
  };

/**
 * Typeguard function that will check if a ReactNode is a TimeframeSelector.CustomTrigger
 * @internal
 */
export function _isTimeframeSelectorCustomTriggerElement(
  child: ReactNode,
): child is ReactElement<TimeframeSelectorCustomTriggerProps> {
  return isElement(child) && child.type === CustomTrigger;
}

/**
 * A trigger component to customize a timeframe selector's trigger.
 * It applies all necessary props on the first and closest child,
 * which, therefore, should be an interactive element.
 * @public
 */
export const CustomTrigger = (props: TimeframeSelectorCustomTriggerProps) => {
  const { children, ...remainingProps } = props;
  const { triggerProps, displayValue, hint } = useTimeframeSelectorContext();
  const {
    state: { isValid },
  } = useFormFieldMessagesContext();

  if (isValidElement<PropsWithChildren>(children)) {
    return cloneElement(children, {
      ..._mergeProps(triggerProps, remainingProps, children.props),
    });
  }

  if (typeof children === 'function') {
    return children(
      { displayValue, isInvalid: !isValid, hint },
      _mergeProps(triggerProps, remainingProps),
    );
  }

  return null;
};

CustomTrigger['displayName'] = 'TimeframeSelector.CustomTrigger';
